import {
  Button,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { TrashIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Modal,
  useModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import useIntl from 'hooks/useIntl';
import ConditionalWrapper from 'components/ConditionalWrapper';
import { useDeleteForecastMutation } from './hooks.useMutation';
import usePermissionsHook from 'components/ElementWithPermissions/usePermissionsHook';
import messages from './messages';
import type {
  DeleteForecastButtonProps,
  DeleteForecastModalProps,
} from './types';

export const DeleteForecastModal = ({
  open,
  close,
  onSuccess,
  rentForecastName,
  rentForecastId,
  hasApprovals,
}: DeleteForecastModalProps) => {
  const intl = useIntl();

  const { mutate, isLoading: submitting } = useDeleteForecastMutation({
    close,
    onSuccess,
  });

  return (
    <Modal
      open={open}
      onClose={() => !submitting && close()}
      title={<FormattedMessage {...messages.modalTitle} />}
      icon={<TrashIcon />}
      ModalProps={{
        PaperProps: {
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 480,
            },
            maxHeight: 'calc(100vh - 100px)',
          },
        },
      }}
      actions={
        <>
          <Button variant="text" onClick={close} disabled={submitting}>
            <FormattedMessage {...messages.modalButtonCancel} />
          </Button>
          <ConditionalWrapper
            condition={hasApprovals}
            wrapper={(children) => (
              <Tooltip
                title={intl.formatMessage(messages.modalButtonConfirmApprovals)}
                placement="top"
                arrow
              >
                {children}
              </Tooltip>
            )}
          >
            <Button
              variant="deleteSubtle"
              onClick={() => mutate({ rentForecastId, rentForecastName })}
              disabled={submitting || !!hasApprovals}
            >
              <FormattedMessage {...messages.modalButtonConfirm} />
            </Button>
          </ConditionalWrapper>
        </>
      }
    >
      <Typography>
        <FormattedHTMLMessage
          {...messages.modalBody}
          values={{ forecastName: rentForecastName }}
        />
      </Typography>
    </Modal>
  );
};

export const DeleteForecastButton = ({
  rentForecastName,
  rentForecastId,
  onSuccess,
  disabled,
  hasApprovals,
}: DeleteForecastButtonProps) => {
  const {
    open: deleteForecastOpen,
    openModal: openDeleteForecast,
    closeModal: closeDeleteForecast,
  } = useModal();

  const hasPermission = usePermissionsHook({
    scope: ['forecast-rents-delete'],
  });

  return (
    <>
      <ConditionalWrapper
        condition={!hasPermission}
        wrapper={(children) => (
          <Tooltip
            title={<FormattedMessage {...messages.tooltipDisabled} />}
            placement="top"
            arrow
          >
            {children}
          </Tooltip>
        )}
      >
        <Button
          disabled={disabled || !hasPermission}
          size="small"
          startIcon={<TrashIcon />}
          onClick={openDeleteForecast}
        >
          <FormattedMessage {...messages.button} />
        </Button>
      </ConditionalWrapper>
      <DeleteForecastModal
        open={deleteForecastOpen}
        close={closeDeleteForecast}
        onSuccess={onSuccess}
        rentForecastName={rentForecastName}
        rentForecastId={rentForecastId}
        hasApprovals={hasApprovals}
      />
    </>
  );
};
